/**
 * /admin/requests
 *
 * Admin review queue for membership requests.
 * Shows pending requests at top, then history (approved/denied).
 * Approve opens a small inline form for role + expiry.
 * Deny has an optional note.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { ApiError } from '../../api/client.js';
import {
  listInviteRequests,
  approveInviteRequest,
  denyInviteRequest,
} from '../../api/invite-requests.js';
import { usePageTitle } from '../../hooks/usePageTitle.js';
import './InviteRequestsAdminPage.css';
import PhotosGroupPicker from '../../components/PhotosGroupPicker.jsx';


function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const today = new Date();
  const diffMs = today - d;
  const diffDays = Math.floor(diffMs / 86_400_000);

  if (diffDays < 1) return 'today';
  if (diffDays < 2) return 'yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}


export default function InviteRequestsAdminPage() {
  usePageTitle('Admin · Requests');

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const loadRequests = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await listInviteRequests();
      setRequests(data);
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.detail : 'failed to load requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadRequests(); }, [loadRequests]);

  const pending = useMemo(() => requests.filter((r) => r.status === 'pending'), [requests]);
  const history = useMemo(() => requests.filter((r) => r.status !== 'pending'), [requests]);

  return (
    <>
      <div className="admin-page-head">
        <div className="admin-crumbs">
          <a href="/admin">admin</a>
          <span className="sep">/</span>
          requests
        </div>
        <div className="admin-section-head">
          <div>
            <h1 className="admin-section-title">Membership Requests</h1>
            <p className="admin-section-sub">
              people who want in — approve or deny at your leisure
            </p>
          </div>
        </div>
      </div>

      <div className="admin-content">
        {/* ── pending ── */}
        <section className="ir-section">
          <h2 className="ir-section-title">
            Pending
            {!loading && (
              <span className="ir-count">{pending.length}</span>
            )}
          </h2>

          {loading ? (
            <div className="ir-empty">loading…</div>
          ) : loadError ? (
            <div className="ir-empty error">⚠ {loadError}</div>
          ) : pending.length === 0 ? (
            <div className="ir-empty">
              no pending requests. either nobody wants in or you're just that fast.
            </div>
          ) : (
            <div className="ir-cards">
              {pending.map((req) => (
                <RequestCard
                  key={req.id}
                  request={req}
                  onAction={loadRequests}
                />
              ))}
            </div>
          )}
        </section>

        {/* ── history ── */}
        {history.length > 0 && (
          <section className="ir-section">
            <h2 className="ir-section-title">
              History
              <span className="ir-count">{history.length}</span>
            </h2>

            <div className="ir-table">
              <div className="ir-row ir-row-head">
                <div>Email</div>
                <div>Username</div>
                <div>Requested</div>
                <div>Status</div>
                <div>Reviewed</div>
              </div>
              {history.map((req) => (
                <div key={req.id} className="ir-row">
                  <div className="ir-email">{req.email}</div>
                  <div className="ir-username">{req.username}</div>
                  <div className="ir-date">{formatDate(req.created_at)}</div>
                  <div>
                    <span className={`ir-status ${req.status}`}>{req.status}</span>
                  </div>
                  <div className="ir-date">{formatDate(req.reviewed_at)}</div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
}


// ─── individual request card (for pending) ─────────────────────────────

function RequestCard({ request, onAction }) {
  const [mode, setMode] = useState(null); // null | 'approve' | 'deny'
  const [role, setRole] = useState('standard');
  const [expiresInDays, setExpiresInDays] = useState(7);
  const [reviewNote, setReviewNote] = useState('');
  const [photosGroups, setPhotosGroups] = useState([]);
  const [acting, setActing] = useState(false);
  const [error, setError] = useState('');

  async function handleApprove() {
    setActing(true);
    setError('');
    try {
      await approveInviteRequest(request.id, {
        role,
        expires_in_days: Number(expiresInDays),
        review_note: reviewNote.trim() || null,
        module_config: photosGroups.length > 0 ? { photos: { group_memberships: photosGroups } } : null,
      });
      onAction();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : 'failed to approve');
    } finally {
      setActing(false);
    }
  }

  async function handleDeny() {
    setActing(true);
    setError('');
    try {
      await denyInviteRequest(request.id, {
        review_note: reviewNote.trim() || null,
      });
      onAction();
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : 'failed to deny');
    } finally {
      setActing(false);
    }
  }

  return (
    <div className="ir-card">
      <div className="ir-card-top">
        <div className="ir-card-identity">
          <div className="ir-card-name">{request.display_name}</div>
          <div className="ir-card-meta">
            {request.username} · {request.email}
          </div>
          <div className="ir-card-meta">
            {request.phone_number} · requested {formatDate(request.created_at)}
          </div>
        </div>
      </div>

      {request.note && (
        <div className="ir-card-note">
          <span className="ir-note-label">note:</span> {request.note}
        </div>
      )}

      {error && (
        <div className="ir-card-error">⚠ {error}</div>
      )}

      {/* ── default actions ── */}
      {mode === null && (
        <div className="ir-card-actions">
          <button
            className="admin-btn admin-btn-primary"
            onClick={() => setMode('approve')}
          >
            approve
          </button>
          <button
            className="admin-btn admin-btn-danger-ghost"
            onClick={() => setMode('deny')}
          >
            deny
          </button>
        </div>
      )}

      {/* ── approve form ── */}
      {mode === 'approve' && (
        <div className="ir-card-form">
          <div className="ir-card-form-row">
            <div className="ir-mini-field">
              <label className="ir-mini-label">role</label>
              <select value={role} onChange={(e) => setRole(e.target.value)}>
                <option value="standard">standard</option>
                <option value="admin">admin</option>
                <option value="guest">guest</option>
              </select>
            </div>
            <div className="ir-mini-field">
              <label className="ir-mini-label">invite expires</label>
              <select
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(e.target.value)}
              >
                <option value="3">3 days</option>
                <option value="7">7 days</option>
                <option value="14">14 days</option>
                <option value="30">30 days</option>
              </select>
            </div>
          </div>
          <div className="ir-mini-field ir-mini-field-wide">
            <label className="ir-mini-label">admin note (optional)</label>
            <input
              type="text"
              placeholder="internal note — not sent to the user"
              value={reviewNote}
              onChange={(e) => setReviewNote(e.target.value)}
            />
          </div>
          <PhotosGroupPicker value={photosGroups} onChange={setPhotosGroups} />
          <div className="ir-card-actions">
            <button
              className="admin-btn admin-btn-primary"
              onClick={handleApprove}
              disabled={acting}
            >
              {acting ? 'approving…' : 'confirm approve'}
            </button>
            <button
              className="admin-btn admin-btn-ghost"
              onClick={() => setMode(null)}
              disabled={acting}
            >
              cancel
            </button>
          </div>
        </div>
      )}

      {/* ── deny form ── */}
      {mode === 'deny' && (
        <div className="ir-card-form">
          <div className="ir-mini-field ir-mini-field-wide">
            <label className="ir-mini-label">admin note (optional)</label>
            <input
              type="text"
              placeholder="reason for denial — internal only"
              value={reviewNote}
              onChange={(e) => setReviewNote(e.target.value)}
            />
          </div>
          <div className="ir-card-actions">
            <button
              className="admin-btn admin-btn-danger"
              onClick={handleDeny}
              disabled={acting}
            >
              {acting ? 'denying…' : 'confirm deny'}
            </button>
            <button
              className="admin-btn admin-btn-ghost"
              onClick={() => setMode(null)}
              disabled={acting}
            >
              cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
